import React, { useEffect, useMemo } from 'react';
import { Autocomplete, Avatar, InputAdornment, InputProps, TextField, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { loadCountries } from 'store/actions';
import { AppDispatch } from 'store';
import { Country } from 'types';
import { Box } from '@mui/system';

export type CountryOption = {
    id: string | undefined;
    value: string | undefined | Country;
    label: string;
    country: Country;
};
type CountrySelectProps = {
    countries: Country[];
    loadCountries: VoidFunction;
    valueAsAlpha2?: boolean;
    valueAsObject?: boolean;
    value?: string;
    placeholder?: string;
    onChange?: Function;
    disabled?: boolean;
    inputProps?: InputProps;
};
function CountrySelect(props: CountrySelectProps) {
    useEffect(() => {
        props.loadCountries();
    }, []);
    let options: Array<CountryOption> = useMemo(() => {
        let countries: Country[] = [];
        if (props.countries && props.countries.length) {
            countries = props.countries;
        }
        return countries.map((item) => {
            let value: string | Country | undefined = item._id;
            if (props.valueAsObject) {
                value = item;
            } else if (props.valueAsAlpha2) {
                value = item.alpha_2;
            }
            return {
                id: item._id,
                value: value,
                label: item.name,
                country: item
            };
        });
    }, [props.countries]);

    let found = options.find((opt) => {
        let selectedCountry = props.countries.find((item: Country) => {
            if (props.valueAsAlpha2) {
                return item.alpha_2 === props.value;
            }
            return item._id == props.value;
        });
        return selectedCountry?._id === opt.id;
    });
    if (props.value && !found) {
        return null;
    }
    return (
        <Autocomplete
            disableClearable
            size="small"
            value={found}
            onChange={(e: React.SyntheticEvent, newValue: CountryOption) => {
                props.onChange?.(newValue);
            }}
            options={options}
            disabled={props.disabled}
            onOpen={() => (document.body.style.overflow = 'hidden')}
            onClose={() => (document.body.style.overflow = 'auto')}
            disablePortal
            ListboxProps={{
                style: { maxHeight: '300px', overflowY: 'auto' } // Makes list scrollable
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputProps={{
                        ...params.inputProps,
                        placeholder: props.placeholder
                    }}
                    InputProps={{
                        ...params.InputProps,
                        ...props.inputProps,
                        startAdornment: found ? (
                            <InputAdornment position="start">
                                <Avatar
                                    src={`https://hatscripts.github.io/circle-flags/flags/${found.country.alpha_2.toLowerCase()}.svg`}
                                    sx={{ width: 24, height: 24 }}
                                />
                            </InputAdornment>
                        ) : null
                    }}
                />
            )}
            renderOption={(optProps, option) => (
                <Box component="li" {...optProps} display="flex" alignItems="center">
                    <Avatar
                        src={`https://hatscripts.github.io/circle-flags/flags/${option.country.alpha_2.toLowerCase()}.svg`}
                        sx={{ width: 24, height: 24, mr: 1 }}
                    />
                    <Typography variant="body1">{option.label}</Typography>
                </Box>
            )}
        />
    );
}

function mapStateToProps(state: { countries: Country[] }) {
    return { countries: state.countries };
}
function mapDispatchToProps(dispatch: AppDispatch) {
    return bindActionCreators({ loadCountries }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(CountrySelect);
